import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Zap, ArrowRight, Loader2, ChevronDown, Search, X, Check, AlertCircle, Trash2, Plus, FileText } from 'lucide-react';
import { useAuth, API } from '../App';
import Header from '../components/layout/Header';
import { isValidModelId, getInvalidModelVoices, getModelValidationMessage } from '../lib/modelValidation';
import { DebateConfig } from '../components/DebateConfig';
import { ContextAttacher } from '../components/ContextAttacher';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };
const TIER_ORDER = { economy: 0, standard: 1, premium: 2 };
const VOICE_COLORS = ['#3B82F6', '#EF4444', '#22C55E', '#A855F7', '#F59E0B', '#06B6D4', '#EC4899'];

export default function ImportPrompt() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Step 1: paste prompt
  const [rawPrompt, setRawPrompt] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');

  // Step 2: review/edit parsed config
  const [parsedConfig, setParsedConfig] = useState(null);
  const [title, setTitle] = useState('');
  const [scenario, setScenario] = useState('');
  const [voices, setVoices] = useState([]);
  const [phases, setPhases] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);

  // Step 3: launch
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState('');
  const [debateMode, setDebateMode] = useState('panel');
  const [frictionLevel, setFrictionLevel] = useState(3);
  const [catFightTheme, setCatFightTheme] = useState('boxing_ring');
  const [contextIds, setContextIds] = useState([]);

  // Check for pre-loaded config from Prompt Builder
  useEffect(() => {
    if (searchParams.get('from') === 'builder') {
      const stored = sessionStorage.getItem('fray_builder_config');
      if (stored) {
        try {
          const config = JSON.parse(stored);
          applyParsedConfig(config);
          sessionStorage.removeItem('fray_builder_config');
        } catch {}
      }
    }
  }, [searchParams]);

  useEffect(() => {
    if (user) loadModels();
  }, [user]);

  const loadModels = async () => {
    try {
      const res = await API.get('/api/byok/models');
      const sorted = (res.data.models || []).sort((a, b) => {
        const td = (TIER_ORDER[a.tier] || 0) - (TIER_ORDER[b.tier] || 0);
        return td !== 0 ? td : a.name.localeCompare(b.name);
      });
      setAvailableModels(sorted);
    } catch {}
  };

  const handleParse = async () => {
    if (!rawPrompt.trim() || rawPrompt.length < 50) {
      setParseError('Paste a prompt with at least 50 characters');
      return;
    }
    setParsing(true);
    setParseError('');
    try {
      const res = await API.post('/api/frays/parse-prompt', { raw_prompt: rawPrompt });
      applyParsedConfig(res.data);
    } catch (err) {
      setParseError(err.response?.data?.detail || 'Failed to parse prompt');
    }
    setParsing(false);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) { setParseError('File too large (max 500KB)'); return; }

    setParsing(true);
    setParseError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await API.post('/api/frays/parse-file', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      applyParsedConfig(res.data);
    } catch (err) {
      setParseError(err.response?.data?.detail || 'Failed to parse file');
    }
    setParsing(false);
    e.target.value = '';
  };

  const applyParsedConfig = (data) => {
    setParsedConfig(data);
    setTitle(data.title || 'Imported Fray');
    setScenario(data.scenario || '');
    setVoices(data.voices || []);
    setPhases(data.phases || []);
    if (data.parse_warning) setParseError(data.parse_warning);

    // Auto-save to context repo if parsed from paste/upload (not from builder)
    if (rawPrompt.length > 100) {
      API.post('/api/contexts', {
        title: data.title || 'Imported Prompt',
        content: rawPrompt,
        type: 'prompt',
      }).catch(() => {}); // silent save
    }
  };

  const updateVoice = (idx, field, value) => {
    setVoices(prev => prev.map((v, i) => i === idx ? { ...v, [field]: value } : v));
  };

  const updateVoiceModel = (idx, modelId, modelName, tier) => {
    setVoices(prev => prev.map((v, i) =>
      i === idx ? { ...v, model: modelId, model_tier: tier } : v
    ));
  };

  const removeVoice = (idx) => {
    if (voices.length <= 2) return;
    setVoices(prev => prev.filter((_, i) => i !== idx));
  };

  const addVoice = () => {
    if (voices.length >= 7) return;
    setVoices(prev => [...prev, {
      name: `Voice ${prev.length + 1}`,
      mandate: '',
      model: 'google/gemini-2.5-flash',
      model_tier: 'economy',
      color: VOICE_COLORS[prev.length % VOICE_COLORS.length],
    }]);
  };

  const handleLaunch = async () => {
    if (!scenario.trim()) { setError('Scenario is required'); return; }
    if (voices.length < 2) { setError('Need at least 2 voices'); return; }

    // Validate all model IDs
    const invalid = getInvalidModelVoices(voices);
    if (invalid.length > 0) {
      setError(getModelValidationMessage(invalid));
      return;
    }

    setLaunching(true);
    setError('');
    try {
      const res = await API.post('/api/frays', {
        scenario: scenario,
        num_phases: phases.length || 3,
        debate_mode: debateMode,
        friction_level: debateMode === 'cat_fight' ? 10 : frictionLevel,
        cat_fight_theme: debateMode === 'cat_fight' ? catFightTheme : undefined,
        context_ids: contextIds,
        custom_voices: voices.map(v => ({
          name: v.name,
          mandate: v.mandate || `Provide expert analysis as ${v.name}.`,
          model: v.model,
          model_tier: v.model_tier,
          color: v.color,
        })),
      });
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create Fray');
      setLaunching(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F0F]">
      <Header />
      <div className="pt-20 pb-12 px-6 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-mono text-[#FF4D00]">IMPORT</span>
              <span className="text-[10px] px-2 py-0.5 border border-[#FF4D00]/30 text-[#FF4D00] font-mono">PROMPT PARSER</span>
            </div>
            <h1 className="text-3xl font-bold font-['Barlow_Condensed']" data-testid="import-title">Import Prompt</h1>
            <p className="text-[#A0A0A0] text-base mt-2">
              Paste an externally developed prompt — we'll parse it to extract the committee, phases, and scenario, then populate the Fray wizard so you can review and launch.
            </p>
          </div>

          {/* Step 1: Paste prompt (show when no parsed config) */}
          {!parsedConfig && (
            <div data-testid="paste-step">
              <div className="mb-4">
                <label className="text-sm font-semibold font-['Barlow_Condensed'] block mb-2">Paste Your Prompt</label>
                <p className="text-xs text-[#A0A0A0] mb-3">
                  Paste a markdown document, debate specification, committee design, or any structured prompt.
                  The parser will extract voices, mandates, phases, and scenario context.
                </p>
                <textarea
                  value={rawPrompt}
                  onChange={e => setRawPrompt(e.target.value)}
                  className="w-full h-80 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-[#F5F5F5] text-sm resize-none focus:outline-none focus:border-[#FF4D00] transition-colors font-mono leading-relaxed"
                  placeholder={"# Example: Brand Identity Sprint\n\n## Committee Design\n1. **Creative Director** (gpt5.4) — Ex-agency creative...\n2. **Brand Strategist** (claude) — Named 40+ startups...\n\n## Phase Plan\n### Phase 1: Name Generation\nEach member proposes 4-5 names..."}
                  data-testid="raw-prompt-input"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] font-mono text-[#A0A0A0]">{rawPrompt.length} characters</span>
                  <div className="flex items-center gap-2 text-[10px] font-mono text-[#A0A0A0]">
                    <FileText className="w-3 h-3" />
                    Supports: Markdown, plain text, structured specs
                  </div>
                </div>
              </div>

              {parseError && (
                <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm flex items-center gap-2" data-testid="parse-error">
                  <AlertCircle className="w-4 h-4" /> {parseError}
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleParse}
                  disabled={parsing || rawPrompt.length < 50}
                  className="px-8 py-3 bg-[#FF4D00] text-black font-semibold flex items-center gap-2 hover:bg-[#FF4D00]/90 transition-colors disabled:opacity-40"
                  data-testid="parse-prompt-btn"
                >
                  {parsing ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Parsing...</>
                  ) : (
                    <><Upload className="w-4 h-4" /> Parse & Configure</>
                  )}
                </button>

                <span className="text-xs text-[#A0A0A0]">or</span>

                <label className={`px-6 py-3 border border-[#2A2A2A] text-[#A0A0A0] font-medium text-sm flex items-center gap-2 hover:border-[#FF4D00]/40 hover:text-[#F5F5F5] transition-colors cursor-pointer ${parsing ? 'opacity-40 pointer-events-none' : ''}`}
                  data-testid="upload-file-btn">
                  <FileText className="w-4 h-4" /> Upload File
                  <input
                    type="file"
                    accept=".md,.txt,.markdown,.text"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={parsing}
                    data-testid="file-input"
                  />
                </label>
              </div>
            </div>
          )}

          {/* Step 2: Review parsed config */}
          {parsedConfig && (
            <div data-testid="review-step">
              <div className="p-3 bg-[#22C55E]/10 border border-[#22C55E]/30 mb-6 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#22C55E]" />
                  <span className="text-sm text-[#22C55E]">
                    Parsed: {voices.length} voices, {phases.length} phases extracted
                  </span>
                </div>
                <button
                  onClick={() => { setParsedConfig(null); setTitle(''); setScenario(''); setVoices([]); setPhases([]); }}
                  className="text-xs text-[#A0A0A0] hover:text-[#FF4D00]"
                  data-testid="reset-parse-btn"
                >Re-paste prompt</button>
              </div>

              {/* Title */}
              <div className="mb-6">
                <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Title</label>
                <input
                  type="text" value={title} onChange={e => setTitle(e.target.value)}
                  className="w-full px-4 py-2 bg-[#1A1A1A] border border-[#2A2A2A] text-sm font-['Barlow_Condensed'] font-medium focus:outline-none focus:border-[#FF4D00]"
                  data-testid="import-title-input"
                />
              </div>

              {/* Scenario */}
              <div className="mb-8">
                <label className="text-xs text-[#A0A0A0] font-mono mb-1 block">Scenario / Context</label>
                <textarea
                  value={scenario} onChange={e => setScenario(e.target.value)}
                  className="w-full h-36 p-4 bg-[#1A1A1A] border border-[#2A2A2A] text-sm text-[#F5F5F5] resize-none focus:outline-none focus:border-[#FF4D00] leading-relaxed"
                  data-testid="import-scenario"
                />
              </div>

              {/* Voices */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold font-['Barlow_Condensed']">Committee Voices</h2>
                  <span className="text-xs font-mono text-[#A0A0A0]">{voices.length} / 7</span>
                </div>

                <div className="space-y-3">
                  {voices.map((v, i) => (
                    <div key={i} className="p-4 bg-[#1A1A1A] border border-[#2A2A2A]" data-testid={`import-voice-${i}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center border-2 shrink-0 text-xs font-bold font-['Barlow_Condensed'] mt-1"
                          style={{ borderColor: v.color, color: v.color }}>
                          {(v.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <input type="text" value={v.name} onChange={e => updateVoice(i, 'name', e.target.value)}
                              className="flex-1 px-3 py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm font-['Barlow_Condensed'] font-medium focus:outline-none focus:border-[#FF4D00]"
                              data-testid={`import-voice-name-${i}`} />
                            {v.model_hint && (
                              <span className={`text-[9px] px-1.5 py-0.5 border font-mono shrink-0 ${
                                v.model_matched ? 'bg-[#22C55E]/10 text-[#22C55E] border-[#22C55E]/20' : 'bg-[#EF4444]/10 text-[#EF4444] border-[#EF4444]/20'
                              }`} title={v.model_matched ? `Matched: ${v.model_hint} -> ${v.model}` : `Unmatched hint: "${v.model_hint}" — select the correct model below`}>
                                {v.model_hint} {v.model_matched ? '' : '(select below)'}
                              </span>
                            )}
                            {voices.length > 2 && (
                              <button onClick={() => removeVoice(i)} className="p-1 text-[#A0A0A0] hover:text-[#EF4444]">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <textarea value={v.mandate} onChange={e => updateVoice(i, 'mandate', e.target.value)}
                            className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs resize-none focus:outline-none focus:border-[#FF4D00] h-16 leading-relaxed"
                            data-testid={`import-voice-mandate-${i}`} />
                          <ModelPickerCompact
                            model={v.model} tier={v.model_tier} index={i}
                            availableModels={availableModels}
                            onSelect={(id, name, tier) => updateVoiceModel(i, id, name, tier)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {voices.length < 7 && (
                  <button onClick={addVoice}
                    className="w-full mt-3 py-2 border border-dashed border-[#2A2A2A] text-sm text-[#A0A0A0] hover:text-[#FF4D00] hover:border-[#FF4D00]/40 flex items-center justify-center gap-2"
                    data-testid="add-voice-import">
                    <Plus className="w-4 h-4" /> Add Voice
                  </button>
                )}
              </div>

              {/* Phases */}
              <div className="mb-8">
                <h2 className="text-lg font-semibold font-['Barlow_Condensed'] mb-3">Phases</h2>
                <div className="space-y-2">
                  {phases.map((p, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 border-l-2" style={{ borderColor: i === phases.length - 1 ? '#FF4D00' : '#2A2A2A' }}>
                      <span className="text-xs font-mono text-[#A0A0A0] shrink-0 mt-1">{String(i + 1).padStart(2, '0')}</span>
                      <div className="flex-1">
                        <span className="text-sm font-medium font-['Barlow_Condensed']">{p.name}</span>
                        <p className="text-xs text-[#A0A0A0] mt-0.5">{p.instructions}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Launch */}
              {error && <div className="p-3 mb-4 bg-[#EF4444]/10 border border-[#EF4444]/30 text-[#EF4444] text-sm">{error}</div>}

              {/* Debate Mode + Friction + Context */}
              <DebateConfig debateMode={debateMode} setDebateMode={setDebateMode} frictionLevel={frictionLevel} setFrictionLevel={setFrictionLevel} catFightTheme={catFightTheme} setCatFightTheme={setCatFightTheme} />
              <ContextAttacher selectedIds={contextIds} onChange={setContextIds} />

              <div className="p-6 bg-[#1A1A1A] border border-[#2A2A2A]">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-1">Launch: {title}</h3>
                    <p className="text-sm text-[#A0A0A0]">
                      {voices.length} Voices, {phases.length} phases
                      {user?.billing_mode === 'byok' ? ' — using your API keys' : ''}
                    </p>
                  </div>
                  <button
                    onClick={handleLaunch}
                    disabled={launching || voices.length < 2 || !scenario.trim()}
                    className="px-8 py-3 bg-[#FF4D00] text-black font-semibold flex items-center gap-2 hover:bg-[#FF4D00]/90 disabled:opacity-40"
                    data-testid="launch-imported-fray"
                  >
                    {launching ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sharpening claws...</>
                    ) : (
                      <><Zap className="w-4 h-4" /> Enter the Fray</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

/* Compact model picker for import page */
function ModelPickerCompact({ model, tier, index, availableModels, onSelect }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const ref = useRef(null);
  const searchRef = useRef(null);
  const modelInvalid = !isValidModelId(model);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) { document.addEventListener('mousedown', handler); setTimeout(() => searchRef.current?.focus(), 50); }
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = availableModels.filter(m => {
    const ms = !search || m.id.toLowerCase().includes(search.toLowerCase()) || m.name.toLowerCase().includes(search.toLowerCase());
    const mt = tierFilter === 'all' || m.tier === tierFilter;
    return ms && mt;
  });

  return (
    <div className="relative" ref={ref}>
      {modelInvalid && (
        <div className="text-[9px] text-[#EF4444] font-mono mb-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> Invalid model ID — select a valid model from the dropdown
        </div>
      )}
      <button onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-3 py-2 border text-left transition-all group ${
          modelInvalid ? 'border-[#EF4444] bg-[#EF4444]/5 animate-pulse' :
          open ? 'border-[#FF4D00] bg-[#FF4D00]/5' : 'border-[#2A2A2A] hover:border-[#FF4D00]/50'
        }`}
        data-testid={`import-model-picker-${index}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[9px] px-1.5 py-0.5 border font-mono shrink-0"
            style={{ color: TIER_COLORS[tier], borderColor: TIER_COLORS[tier] + '40' }}>{tier?.toUpperCase()}</span>
          <span className="text-xs font-mono text-[#F5F5F5] truncate">{model}</span>
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-[#A0A0A0] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className="absolute z-50 left-0 right-0 mt-1 bg-[#1A1A1A] border border-[#2A2A2A] shadow-xl shadow-black/50 max-h-[260px] flex flex-col">
            {availableModels.length === 0 ? (
              <div className="p-4 text-center text-xs text-[#A0A0A0]">Add API keys in Settings to browse models.</div>
            ) : (
              <>
                <div className="p-2 border-b border-[#2A2A2A]">
                  <div className="relative">
                    <Search className="w-3 h-3 text-[#A0A0A0] absolute left-2 top-1/2 -translate-y-1/2" />
                    <input ref={searchRef} type="text" value={search} onChange={e => setSearch(e.target.value)}
                      className="w-full pl-7 pr-7 py-1 bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] focus:outline-none focus:border-[#FF4D00]" placeholder="Search..." />
                    {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-2.5 h-2.5 text-[#A0A0A0]" /></button>}
                  </div>
                  <div className="flex gap-1 mt-1.5">
                    {['all','economy','standard','premium'].map(t => (
                      <button key={t} onClick={() => setTierFilter(t)}
                        className={`px-1.5 py-0.5 text-[8px] font-mono border ${tierFilter === t ? (t === 'all' ? 'bg-[#FF4D00] text-black border-[#FF4D00]' : 'border-current') : 'border-[#2A2A2A] text-[#A0A0A0]'}`}
                        style={tierFilter === t && t !== 'all' ? { color: TIER_COLORS[t] } : {}}>
                        {t === 'all' ? 'ALL' : t.toUpperCase().slice(0,4)}</button>
                    ))}
                  </div>
                </div>
                <div className="overflow-y-auto flex-1">
                  {filtered.slice(0, 40).map((m) => (
                    <button key={m.id} onClick={() => { onSelect(m.id, m.name, m.tier); setOpen(false); setSearch(''); }}
                      className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left border-b border-[#0F0F0F] ${model === m.id ? 'bg-[#FF4D00]/10' : 'hover:bg-[#2A2A2A]/50'}`}>
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span className="text-[7px] w-10 text-center px-1 py-0.5 border font-mono shrink-0"
                          style={{ color: TIER_COLORS[m.tier], borderColor: TIER_COLORS[m.tier] + '40' }}>{m.tier?.toUpperCase().slice(0,4)}</span>
                        <span className="text-[10px] text-[#F5F5F5] truncate">{m.name || m.id}</span>
                      </div>
                      {model === m.id && <Check className="w-3 h-3 text-[#FF4D00] shrink-0" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
